import type { MoveHistoryEntry } from '../../../../deps/game/moveHistory/moveHistory.types'

export interface CapturedIssueState {
  stateNumber: number
  seqIndex: number | null
  state: unknown
}

export interface CapturedIssueMove {
  seqIndex: number
  move: unknown
}

export interface CapturedIssueHistory {
  lastStates: CapturedIssueState[]
  lastActions: CapturedIssueMove[]
}

/**
 * Captures the most recent successful moves with their post-move state snapshots.
 * State numbers and move indices retain their positions in the room action log.
 */
export function captureIssueHistory(
  entries: MoveHistoryEntry[],
  currentGameState: unknown,
  initialGameState: unknown,
  maximumMoves = 5,
): CapturedIssueHistory {
  const entriesWithSnapshots = entries
    .filter((entry) =>
      entry.success && entry.gameState !== undefined && entry.sequenceIndex !== undefined,
    )
    .slice(-maximumMoves)

  const lastStates: CapturedIssueState[] = entriesWithSnapshots.map((entry) => ({
    stateNumber: entry.sequenceIndex! + 1,
    seqIndex: entry.sequenceIndex!,
    state: entry.gameState,
  }))

  if (lastStates[0]?.seqIndex === 0 && initialGameState !== undefined) {
    lastStates.unshift({ stateNumber: 0, seqIndex: null, state: initialGameState })
  }

  if (lastStates.length === 0 && currentGameState !== undefined) {
    lastStates.push({ stateNumber: 0, seqIndex: null, state: currentGameState })
  } else if (lastStates.length > 0 && currentGameState !== undefined) {
    lastStates[lastStates.length - 1].state = currentGameState
  }

  return {
    lastStates,
    lastActions: entriesWithSnapshots.map((entry) => ({
      seqIndex: entry.sequenceIndex!,
      move: entry.move,
    })),
  }
}
